/**
 * Saved-signature data layer — ports the Forms Hub mockup's
 * `formsHub.signatures.v1` store (getSignatureProfile / saveSignatureProfile).
 *
 * Persistence is per-signer, keyed by the signed-in user, so "view as" / dev
 * impersonation each carry their own signature. ehr#278: the saved signature is
 * also kept on the server (GET/PUT /api/v1/me/signature), so the Provider Profile,
 * signed notes and every browser show the same one: a save goes up straight away,
 * and syncSignature lines this device up with the server (the newer one wins).
 */
import { api } from './api.js';

const STORE_KEY = 'credify.signatures.v1';

/** Typed-signature font choices (mockup SIG_FONTS) — id + CSS font stack. */
export const SIG_FONTS = [
  { id: 'flowing', label: 'Flowing', css: "'Segoe Script','Brush Script MT',cursive" },
  { id: 'classic', label: 'Classic', css: "'Monotype Corsiva','Brush Script MT',cursive" },
  { id: 'handwritten', label: 'Handwritten', css: "'Lucida Handwriting','Segoe Script',cursive" },
  { id: 'brush', label: 'Brush', css: "'Brush Script MT','Segoe Script',cursive" },
] as const;

export type SigMethod = 'typed' | 'uploaded' | 'drawn';

/** The persisted signature + signing preference for one signer. */
export interface SignatureProfile {
  method: SigMethod | null;
  dataUrl: string | null;
  typedText: string;
  typedFont: string;
  /** each_time → build a fresh signature per sign-off; auto → apply the saved one. */
  mode: 'each_time' | 'auto';
  updatedAt: string | null;
}

/** A freshly built (not-yet-saved) signature from the editor. */
export interface SignatureCandidate {
  method: SigMethod;
  typedText: string;
  typedFont: string;
  dataUrl: string;
}

// In-memory fallback when localStorage is unavailable (private mode / quota).
let mem: Record<string, SignatureProfile> = {};

function loadStore(): Record<string, SignatureProfile> {
  try {
    return (
      (JSON.parse(localStorage.getItem(STORE_KEY) || 'null') as Record<string, SignatureProfile>) ||
      {}
    );
  } catch {
    return mem;
  }
}
function persistStore(store: Record<string, SignatureProfile>): void {
  mem = store;
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch {
    /* ignore quota / disabled storage — mem holds the value for this session */
  }
}

const emptyProfile = (): SignatureProfile => ({
  method: null,
  dataUrl: null,
  typedText: '',
  typedFont: SIG_FONTS[0].id,
  mode: 'each_time',
  updatedAt: null,
});

/** The signer's saved profile, or a blank each-time default. */
export function getSignatureProfile(userKey: string): SignatureProfile {
  const p = loadStore()[userKey];
  return p ? { ...emptyProfile(), ...p } : emptyProfile();
}

export function saveSignatureProfile(
  userKey: string,
  profile: Omit<SignatureProfile, 'updatedAt'>,
): Promise<boolean> {
  writeLocal(userKey, { ...profile, updatedAt: new Date().toISOString() });
  return pushSignature(userKey);
}

function writeLocal(userKey: string, profile: SignatureProfile): void {
  const store = loadStore();
  store[userKey] = profile;
  persistStore(store);
}

export function hasSavedSignature(p: SignatureProfile | null | undefined): boolean {
  return !!(p && p.method && p.dataUrl);
}

/** ehr#278: the server's copy (GET/PUT /me/signature). */
export interface ServerSignature {
  method: SigMethod;
  img: string;
  typedText: string;
  typedFont: string;
  mode: 'each_time' | 'auto';
  updatedAt: string;
}

export const fromServer = (s: ServerSignature): SignatureProfile => ({
  method: s.method,
  dataUrl: s.img,
  typedText: s.typedText,
  typedFont: s.typedFont || SIG_FONTS[0].id,
  mode: s.mode === 'auto' ? 'auto' : 'each_time',
  updatedAt: s.updatedAt,
});

/**
 * Send this device's saved signature to the server; true when it took (or there is no
 * signature to send: a signing preference on its own stays on this device). The server's
 * time is kept here too, so the next sync sees the two as the same.
 */
export async function pushSignature(userKey: string): Promise<boolean> {
  const p = getSignatureProfile(userKey);
  if (!hasSavedSignature(p)) return true;
  try {
    const r = await api.put<{ signature: ServerSignature }>('/me/signature', {
      method: p.method,
      img: p.dataUrl,
      typedText: p.typedText,
      typedFont: p.typedFont,
      mode: p.mode,
    });
    writeLocal(userKey, fromServer(r.signature));
    imgCache.delete(userKey); // my notes show the new one
    return true;
  } catch {
    return false;
  }
}

const imgCache = new Map<string, Promise<string | null>>();

/** ehr#278: a signer's saved signature picture, for a note they signed; null when they have none. */
export function signatureImg(userId: string): Promise<string | null> {
  let p = imgCache.get(userId);
  if (!p) {
    p = api.get<{ img: string | null }>(`/providers/${encodeURIComponent(userId)}/signature`).then(
      (r) => r.img ?? null,
      () => null,
    );
    imgCache.set(userId, p);
  }
  return p;
}

/** Which side is newer: 'pull' the server's, 'push' this device's, or nothing to do. */
export function syncDirection(
  local: SignatureProfile,
  server: ServerSignature | null,
): 'pull' | 'push' | null {
  const mine = hasSavedSignature(local);
  if (server && (!mine || (local.updatedAt ?? '') < server.updatedAt)) return 'pull';
  if (mine && (!server || (local.updatedAt ?? '') > server.updatedAt)) return 'push';
  return null;
}

/**
 * Line this device up with the server before anything reads the signature: the newer one
 * wins, and a signature saved only in this browser (before ehr#278) goes up. Offline: no-op.
 */
export function syncSignature(userKey: string): Promise<void> {
  const p = doSync(userKey);
  syncing.set(userKey, p);
  return p;
}

// ehr#404: the sync started on page load, by person. Sign waits for it, so in a fresh browser the
// signature saved elsewhere fills in, rather than Sign opening an empty editor while it's still loading.
const syncing = new Map<string, Promise<void>>();

/** Resolves once this device and the server agree (at once when a sync already finished). */
export function signatureSynced(userKey: string): Promise<void> {
  return syncing.get(userKey) ?? syncSignature(userKey);
}

async function doSync(userKey: string): Promise<void> {
  let server: ServerSignature | null;
  try {
    server = (await api.get<{ signature: ServerSignature | null }>('/me/signature')).signature;
  } catch {
    return;
  }
  const dir = syncDirection(getSignatureProfile(userKey), server);
  if (dir === 'pull' && server) writeLocal(userKey, fromServer(server));
  if (dir === 'push') await pushSignature(userKey);
}

/** Render a typed name in the chosen font to a PNG data URL (mockup typedToDataUrl). */
export function typedToDataUrl(text: string, fontCss: string): string {
  const cv = document.createElement('canvas');
  cv.width = 560;
  cv.height = 160;
  const ctx = cv.getContext('2d');
  if (!ctx) return '';
  ctx.fillStyle = '#1c3faa';
  ctx.font = '48px ' + fontCss;
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 24, 80, 512);
  return cv.toDataURL('image/png');
}
